import { useEffect, useRef, useState } from "react";
import { Lock, Youtube, ShieldCheck, Clock, Mail } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import TrailerPlayer from "../components/TrailerPlayer";
import client, { apiError } from "../lib/api";
import { SOCIAL, STANDUP_CLIPS } from "../data/content";

function StandupClipCard({ clip }) {
  const [play, setPlay] = useState(false);
  const id = `standup-${clip.performer.toLowerCase().replace(/[\s.]+/g, "-")}`;
  return (
    <div className="rounded-xl border border-white/10 bg-[#181824]/70 overflow-hidden" data-testid={id}>
      <div className="relative aspect-video bg-black">
        {play ? (
          <iframe
            src={`https://www.youtube.com/embed/${clip.youtubeId}?autoplay=1&rel=0`}
            title={clip.title}
            className="h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button onClick={() => setPlay(true)} className="group relative h-full w-full" aria-label={`Play ${clip.title}`} data-testid={`${id}-play`}>
            <img src={`https://img.youtube.com/vi/${clip.youtubeId}/hqdefault.jpg`} alt={clip.title} loading="lazy"
              className="h-full w-full object-cover opacity-80 transition-opacity group-hover:opacity-100" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/30">
              <span className="rounded-full bg-[#E11D48] p-4 shadow-[0_0_30px_rgba(225,29,72,0.6)] transition-transform group-hover:scale-110">
                <Youtube size={24} className="text-white" />
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="p-4">
        <p className="font-display text-xl text-white">{clip.title}</p>
        <p className="text-xs text-[#F59E0B] font-semibold uppercase tracking-widest">{clip.performer}</p>
      </div>
    </div>
  );
}

function TrailerSection() {
  return (
    <div>
      <TrailerPlayer testId="trailer-player" />
      <a href={SOCIAL.youtube} target="_blank" rel="noopener noreferrer" data-testid="youtube-channel-link"
        className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-white link-gold">
        <Youtube size={16} className="text-[#E11D48]" /> Official YouTube channel — @laughatlacomedy
      </a>
    </div>
  );
}

function EpisodeUnlock() {
  const [code, setCode] = useState("");
  const episodeRef = useRef(null);
  const [state, setState] = useState({ status: "idle", message: "" }); // idle | error | unlocked | pending
  const [token, setToken] = useState("");
  const [streamUrl, setStreamUrl] = useState("");
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    client.get("/site").then((r) => setAvailable(r.data.episode_available)).catch(() => {});
    if (window.location.hash === "#episode") {
      setTimeout(() => episodeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, []);

  const unlock = async (e) => {
    e.preventDefault();
    setLoading(true);
    setState({ status: "idle", message: "" });
    try {
      const { data } = await client.post("/watch/unlock", { code });
      setToken(data.token);
      setStreamUrl(`${process.env.REACT_APP_BACKEND_URL}/api/watch/stream?token=${encodeURIComponent(data.token)}`);
      setState({ status: "unlocked", message: `Unlocked. Your viewing session lasts ${data.expires_minutes} minutes.` });
    } catch (err) {
      const status = err?.response?.status;
      setState({
        status: status === 503 ? "pending" : "error",
        message: apiError(err, "That code is invalid or has expired."),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="episode" ref={episodeRef} data-testid="episode-player" className="scroll-mt-24 rounded-2xl border border-white/10 bg-[#181824]/70 backdrop-blur-xl p-6 sm:p-10">
      <div className="flex items-start gap-4">
        <span className="rounded-full bg-[#E11D48]/15 p-3 text-[#E11D48]"><Lock size={22} /></span>
        <div>
          <h2 className="font-display text-3xl sm:text-4xl text-white">Full Episode — Code Protected</h2>
          <p className="mt-2 text-gray-400 text-sm sm:text-base">
            Episode 4 of the original season is available to approved viewers. Enter your viewing code below. Codes are issued by Providence Film Group and expire after a single viewing session.
          </p>
        </div>
      </div>

      {state.status !== "unlocked" ? (
        <form onSubmit={unlock} className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="unlock-form">
          <label htmlFor="episode-code" className="sr-only">Viewing code</label>
          <input
            id="episode-code"
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter your viewing code"
            required
            autoComplete="off"
            data-testid="unlock-code-input"
            className="flex-1 rounded-full border border-white/15 bg-black/40 px-6 py-3.5 text-white placeholder-gray-500 outline-none focus:border-[#F59E0B]"
          />
          <button type="submit" disabled={loading} data-testid="unlock-submit"
            className="rounded-full bg-[#E11D48] px-8 py-3.5 font-semibold text-white transition-colors hover:bg-[#F43F5E] disabled:opacity-60">
            {loading ? "Checking…" : "Unlock"}
          </button>
        </form>
      ) : (
        <div className="mt-8" data-testid="episode-video-wrapper">
          <video key={streamUrl} controls controlsList="nodownload" className="w-full rounded-xl border border-white/10 bg-black aspect-video" data-testid="episode-video">
            <source src={streamUrl} type="video/mp4" />
            Your browser does not support video playback.
          </video>
        </div>
      )}

      {state.status === "error" && (
        <p role="alert" className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="unlock-error">{state.message}</p>
      )}
      {state.status === "pending" && (
        <p role="status" className="mt-4 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-4 py-3 text-sm text-[#F59E0B]" data-testid="unlock-pending">{state.message}</p>
      )}
      {state.status === "unlocked" && (
        <p role="status" className="mt-4 flex items-center gap-2 text-sm text-[#F59E0B]" data-testid="unlock-success">
          <Clock size={14} /> {state.message}
        </p>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-white/10 pt-6 text-sm">
        <span className="flex items-center gap-2 text-gray-400"><ShieldCheck size={15} className="text-[#F59E0B]" /> Server-side code check, short-lived viewing session</span>
        <a href={`mailto:${SOCIAL.emailPfg}?subject=Screener%20access%20request%20—%20Laugh%20at%20L.A.`} data-testid="request-screener-link"
          className="flex items-center gap-2 font-semibold text-[#F59E0B] link-gold">
          <Mail size={15} /> Request Screener Access
        </a>
      </div>
    </div>
  );
}

export default function Watch() {
  return (
    <main className="pt-16" data-testid="watch-page">
      <Seo title="Watch" description="Watch the Laugh at L.A. trailer and unlock the code-protected full episode." noindex />
      <section className="spotlight-bg border-b border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20 sm:py-24">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F59E0B]">Watch</p>
            <h1 className="mt-3 font-display text-5xl sm:text-7xl text-white neon-red">Roll the tape.</h1>
          </Reveal>
        </div>
      </section>
      <section className="mx-auto max-w-5xl px-4 sm:px-6 py-16 space-y-16">
        <Reveal>
          <h2 className="font-display text-3xl text-white mb-6">Official Trailer</h2>
          <TrailerSection />
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display text-3xl text-white mb-2">Live on the Mic</h2>
          <p className="text-gray-400 text-sm mb-6">Stand-up sets from the Laugh at L.A. stage.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="standup-clips">
            {STANDUP_CLIPS.map((clip) => <StandupClipCard key={clip.youtubeId} clip={clip} />)}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <EpisodeUnlock />
        </Reveal>
      </section>
    </main>
  );
}
